export interface IInputUnit {
  value: UnitValue;
  title: UnitTitle;
}

export interface IInputUnits {
  [inputCode: number]: IInputUnit[];
}

export interface IGroupUnits {
  [inputGroupCode: number]: IInputUnits;
}

export interface ITransactionUnits {
  [AdvertisementType: string]: IGroupUnits;
}

export interface IAmountUnits extends ITransactionUnits {}

// Transaction Type Codes
export enum AdvertisementType {
  BUY = 'buy',
  SALE = 'sale',
  SERVICE = 'service',
  PROVIDED_SERVICE = 'providedService',
  WANTED_SERVICE = 'wantedService',
  PROVIDED_RENT = 'providedRent',
  WANTED_RENT = 'wantedRent',
}

export enum MainAdvertisementType {
  BUY = 'buy',
  SALE = 'sale',
  SERVICE = 'service',
  RENT = 'rent',
}

// Input Group Codes
export enum InputGroupCode {
  FERTILIZER = 11,
  PESTICIDE = 12,
  SEED = 13,
  EMPLOYEE = 14,
  MACHINERY = 15,
  TOOL = 16,
  FARM = 17,
  LAB = 18,
}

// Input Codes
export enum FertilizerInputCode {
  DEFAULT = 0,
  ANIMAL = 20, // دامی
}

export enum PesticideInputCode {
  DEFAULT = 0,
  OTHER = 99,
}

export enum SeedInputCode {
  SEED = 11, // بذر
  TRANSPLANT = 12, // نشا
  CUTTING_GRAFT = 13, // قلمه و پیوند
  SEEDLING = 14, // نهال
}

export enum EmployeeInputCode {
  DEFAULT = 0,
}

export enum MachineryInputCode {
  DEFAULT = 0,
}

export enum ToolInputCode {
  DEFAULT = 0,

  TIE_STRAPS = 11, // دنباله بندها
  GENERAL_TOOLS = 12, // ابزار عمومی
  PRUNING_TOOLS = 13, // ابزار هرس
  WATER_EQUIPMENT = 14, // تجهیزات آبی

  GROWING_MEDIA = 16, // بستر کشت
  GREENHOUSE = 17, // گلخانه
  ANIMAL_INPUTS = 18, // نهاده های دامی
  LIVESTOCK_FARMING = 19, // دامپروری
}

export enum FarmInputCode {
  FARM = 11, // مزرعه
  WATER = 12, // آب
  IRRIGATION = 13, // آب آبیاری
}

export enum UnitValue {
  // Basic units
  GRAM = 'gram',
  KILOGRAM = 'kilogram',
  TON = 'ton',
  MILLILITER = 'milliliter',
  METER = 'meter',

  // Counting units
  NUMBER = 'number',
  PIECE = 'piece',
  TRUNK = 'trunk',
  BOX = 'box',
  PACK = 'pack',
  UNIT = 'unit',
  HEAD = 'head',
  DEVICE = 'device',

  // Area units
  HECTARE = 'hectare',
  SQUARE_METER = 'square_meter',

  // Time units (for duration)
  SECOND = 'second',
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',

  // Rate units (for services/rent)
  HOURLY = 'hourly',
  DAILY = 'daily',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  PER_HOUR = 'per_hour',
  PER_DAY = 'per_day',
  PER_SECOND = 'per_second',
  PER_HECTARE = 'per_hectare',

  // Units with Alternatives (because of the old apis each might take different alternative)
  LITER = 'liter',
  LITRE = 'litre',

  CUBIC_METER = 'cubic_meter',
  CUBICMETER = 'cubic meter',

  // Special
  UNITLESS = 'unitless',
}

export enum UnitTitle {
  // Basic units
  GRAM = 'گرم',
  KILOGRAM = 'کیلوگرم',
  LITER = 'لیتر',
  TON = 'تن',
  CUBIC_METER = 'مترمکعب',
  CUBICMETER = 'مترمکعب',
  METER = 'متر',
  MILLILITER = 'میلی لیتر',
  LITRE = 'لیتر',

  // Counting units
  NUMBER = 'عدد',
  PIECE = 'قطعه',
  TRUNK = 'اصله',
  BOX = 'بسته',
  PACK = 'بسته',
  UNIT = 'واحد',
  HEAD = 'راس',
  DEVICE = 'دستگاه',

  // Area units
  HECTARE = 'هکتار',
  SQUARE_METER = 'مترمربع',

  // Time units (for duration)
  HOUR = 'ساعت',
  DAY = 'روز',
  MONTH = 'ماه',
  YEAR = 'سال',

  // Rate units (for services/rent)
  SECOND = 'ثانیه',
  HOURLY = 'ساعتی',
  DAILY = 'روزانه',
  MONTHLY = 'ماهانه',
  YEARLY = 'سالانه',
  PER_HOUR = 'در ساعت',
  PER_DAY = 'در روز',
  PER_SECOND = 'در ثانیه',
  PER_HECTARE = 'در هکتار',

  // Special
  UNITLESS = 'بدون واحد',
}

export const AdjectivalUnitTitle: Record<string, string> = {
  gram: 'گرمی',
  kilogram: 'کیلویی',
  liter: 'لیتری',
  litre: 'لیتری',
  ton: 'تنی',
  cubic_meter: 'مترمکعبی',
  meter: 'متری',
  'cubic meter': 'مترمکعبی',
  milliliter: 'میلی‌لیتری',

  number: 'عددی',
  piece: 'قطعه‌ای',
  trunk: 'اصله‌ای',
  box: 'بسته ای',
  pack: 'بسته‌ای',
  unit: 'واحدی',
  head: 'راسی',
  device: 'دستگاهی',
  hectare: 'هکتاری',
  square_meter: 'مترمربعی',

  unitless: 'بدون واحد',
};

export const NounUnitTitle: Record<string, string> = {
  gram: 'گرم',
  kilogram: 'کیلوگرم',
  liter: 'لیتر',
  litre: 'لیتر',
  ton: 'تن',
  cubic_meter: 'متر مکعب',
  'cubic meter': 'متر مکعب',
  meter: 'متر',

  milliliter: 'میلی‌لیتر',

  number: 'عدد',
  piece: 'قطعه',
  trunk: 'اصل',
  box: 'بسته',
  pack: 'بسته',
  unit: 'واحد',
  head: 'راس',
  device: 'دستگاه',
  hectare: 'هکتار',
  square_meter: 'متر مربع',

  unitless: '',
};

export const getQuantityPackUnitByValue = (
  value: UnitValue | string | null,
): IInputUnit | undefined => {
  if (!value) return undefined;
  const key = Object.keys(UnitValue).find(
    k => UnitValue[k as keyof typeof UnitValue] === value,
  ) as keyof typeof UnitValue | undefined;

  if (!key) return undefined;

  return {
    value: UnitValue[key],
    title: UnitTitle[key],
  };
};

/** ابزار عمومی — children of ToolInputCode.GENERAL_TOOLS */
export enum GeneralToolCode {
  PLIERS = 111, // انبر
  WRENCH = 112, // آچار
  PIPE_CLAMP = 113, // بست لوله
  SHOVEL_AND_TROWEL = 114, // بیل و بیلچه
  SCREW_AND_NUT = 115, // پیج و مهره
  GARDENING_GLUE = 116, // چسب باغبانی
  HAMMER = 117, // چکش
  DRILL = 118, // دریل
  GLOVES = 119, // دستکش
  NAIL = 120, // میخ
  LEAF_RAKE = 121, // برگ جمع کن
  MOTORIZED_AUGER = 122, // چاله کن موتور دار
  SHOVEL_HANDLE = 123, // دسته بیل
  FOUNTAIN = 124, // فواره
  SAW = 126, // اره
  INSECTICIDE_GLUE = 127, // چسب حشره کش
  SEEDLING_TRAY = 128, // سینی نشا
  DRIP_IRRIGATION_TAPE = 129, // نوار آبیاری قطره ای
  GREENHOUSE_NYLON = 130, // نایلون گلخانه
  GREENHOUSE_SHADE_NET = 131, // توری سایبان گلخانه
  CRATE = 132, // جعبه
  BACKPACK_SPRAYER = 133, // سمپاش پشتی
}

/** تجهیزات آبی — children of ToolInputCode.WATER_EQUIPMENT */
export enum WaterEquipmentCode {
  SPRINKLER = 111, // آب پاش
  WATER_PUMP = 112, // پمپ آب
  SUMP_PUMP = 113, // پمپ کف کش
  SLUDGE_PUMP = 114, // پمپ لجن کش
  HOSE_CONNECTOR = 115, // رابط شلنگ
  POLYETHYLENE_CONNECTOR = 116, // رابط پلی اتیلن
  IRRIGATION_HOSE = 117, // شلنگ آبیاری
  DRIP_IRRIGATION_PARTS = 118, // لوازم آبیاری قطره ای
  TANK = 119, // مخزن
  MOTOR_PUMP = 120, // موتور پمپ
  PIPE = 121, // لوله
}

/** بستر کشت — children of ToolInputCode.GROWING_MEDIA */
export enum GrowingMediaCode {
  PEAT_MOSS = 111, // پیت ماس
  SEEDLING_TRAY = 112, // سینی نشا
  COCOPEAT = 113, // کوکوپیت
  COCO_CHIPS = 114, // کوکوچیپس
  VERMICOMPOST = 115, // ورمی کمپوست
}

/** گلخانه — children of ToolInputCode.GREENHOUSE */
export enum GreenhouseCode {
  MEASURING_TOOLS = 111, // ابزار اندازه گیری در گلخانه
  FITTINGS = 112, // انواع اتصالات گلخانه
  COVERING_TYPES = 113, // انواع پوشش گلخانه
  NETTING = 114, // انواع توری
  LIGHTING_EQUIPMENT = 115, // انواع وسایل روشنایی
  COOLING_EQUIPMENT = 116, // انواع وسایل سرمایشی
  HEATING_EQUIPMENT = 117, // انواع وسایل گرمایشی
  FOGGER = 118, // فوگر
  STRUCTURE_PARTS = 119, // قطعات سازه گلخانه
  CONSUMABLES = 120, // لوازم مصرفی گلخانه
  GEARBOX_MOTOR = 121, // موتور گیربکس
  COVERING = 122, // پوشش گلخانه
}

/** نهاده دامی — children of ToolInputCode.ANIMAL_INPUTS */
export enum AnimalInputCode {
  LIVESTOCK_FORAGE = 111, // علوفه دام
  OILSEED_MEAL = 112, // کنجاله
  READY_FEED = 113, // خوراک آماده دام
  SUPPLEMENTS_AND_MICRONUTRIENTS = 114, // مکمل ها و ریزمغذی ها
  POULTRY_CONCENTRATE = 116, // انواع کنسانتره طیور
}
